/*
 * This file is part of glowingblue/orell-bookshelf.
 *
 * Copyright (c) 2021 Priska Wermelinger & Rafael Horvat.
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import app from 'flarum/forum/app';
import Component, { type ComponentAttrs } from 'flarum/common/Component';
import SelectDropdown from 'flarum/common/components/SelectDropdown';
import LinkButton from 'flarum/common/components/LinkButton';
import ItemList from 'flarum/common/utils/ItemList';
import Separator from 'flarum/common/components/Separator';
import type Mithril from 'mithril';
import type { BookshelfRouteParams } from '../states/BookshelfPageState';
import type Bookshelf from '../../common/models/Bookshelf';
import type Status from '../../common/models/Status';

export interface BookshelfFilterAttrs extends ComponentAttrs {
	bookshelf: Bookshelf;
}

/** An alias, not an interface: only an alias satisfies `book_collection()`'s `Record` parameter. */
export type BookshelfFilterRouteParams = {
	sort?: string;
	filter?: { status: string | undefined };
};

export default class BookshelfFilter<CustomAttrs extends BookshelfFilterAttrs = BookshelfFilterAttrs> extends Component<CustomAttrs> {
	statuses!: Status[];

	oninit(vnode: Mithril.Vnode<CustomAttrs, this>) {
		super.oninit(vnode);

		this.statuses = app.store.all<Status>('book_status');
	}

	view(): Mithril.Children {
		return (
			<div className="BookshelfFilter">
				<label className="BookshelfFilter-label">{app.translator.trans('glowingblue-orell-bookshelf.forum.bookshelf.filter.label')}</label>
				<SelectDropdown buttonClassName="Button Button--secondary" caretIcon="far fa-chevron-down">
					{this.options.toArray()}
				</SelectDropdown>
			</div>
		);
	}

	get currentFilter(): string | false {
		const routeParams: BookshelfRouteParams | undefined = m.route.param();

		return routeParams?.filter?.status ?? false;
	}

	get options(): ItemList<Mithril.Children> {
		const { bookshelf } = this.attrs;

		const options = new ItemList<Mithril.Children>();

		const params: BookshelfFilterRouteParams = {};
		const sort = m.route.param('sort');
		if (sort) {
			params.sort = sort;
		}

		const allActive = !this.currentFilter;
		options.add(
			'all',
			<LinkButton href={app.route.book_collection(bookshelf, params)} active={allActive} icon={this.getLinkIcon(allActive)}>
				{app.translator.trans('glowingblue-orell-bookshelf.forum.bookshelf.filter.all')}
			</LinkButton>,
			2
		);

		options.add('separator', <Separator />, 1);

		this.statuses.forEach((status) => {
			const statusId = status.id();
			const isActive = this.currentFilter === statusId;

			return options.add(
				String(statusId),
				<LinkButton
					href={app.route.book_collection(bookshelf, {
						...params,
						filter: { status: statusId },
					})}
					active={isActive}
					icon={this.getLinkIcon(isActive)}
				>
					{status.name()}
				</LinkButton>
			);
		});

		options.add('separator-none', <Separator />, -1);

		const noneActive = this.currentFilter === 'none';
		options.add(
			'none',
			<LinkButton
				href={app.route.book_collection(bookshelf, {
					...params,
					filter: { status: 'none' },
				})}
				active={noneActive}
				icon={this.getLinkIcon(noneActive)}
			>
				{app.translator.trans('glowingblue-orell-bookshelf.forum.bookshelf.filter.none')}
			</LinkButton>,
			-2
		);

		return options;
	}

	getLinkIcon(active: boolean): string {
		return active ? 'far fa-dot-circle' : 'far fa-circle';
	}
}
